import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, Phone, Store, Bike, MessageCircle, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { roleHome } from "@/utils/roles";
import { toast } from "sonner";

// Support NelDaari (WhatsApp) — partenaires
const SUPPORT_WHATSAPP = "33758262447";

/** Espace partenaires : connexion dédiée aux comptes restaurant & livreur. */
export default function PartnerLogin() {
  const { t } = useTranslation();
  const { login, user, loading: authLoading } = useAuth();
  const [kind, setKind] = useState("restaurant"); // restaurant | driver (visuel)
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [wrongSpace, setWrongSpace] = useState("");
  const navigate = useNavigate();

  // Déjà connecté → direct sur le tableau de bord du rôle
  useEffect(() => {
    if (!authLoading && user) navigate(roleHome(user.role), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setWrongSpace("");
    try {
      const data = await login(form.identifier, form.password, kind);
      toast.success(t("auth.loginSuccess"));
      navigate(roleHome(data?.role));
    } catch (err) {
      const msg = formatApiError(err);
      if (err?.response?.status === 403) setWrongSpace(msg);
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12 bg-ink">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl">
        <Link to="/" className="font-display text-2xl font-semibold inline-flex items-center gap-2 mb-2">
          <img src="/logo-neldaari.png" alt="NelDaari" className="w-8 h-8" /> NelDaari
        </Link>
        <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-1">{t("pl.spaceLabel")}</div>

        {forgot ? (
          <>
            <h1 className="font-display text-2xl font-semibold mb-2">{t("pl.forgotTitle")}</h1>
            <p className="text-sm text-mute mb-5">{t("pl.forgotHint")}</p>
            <a
              href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
                `Bonjour, je suis ${kind === "restaurant" ? "restaurateur" : "livreur"} partenaire et j'ai oublié mon mot de passe. Mon compte est : ${form.identifier || "…"}`
              )}`}
              target="_blank"
              rel="noreferrer"
              data-testid="partner-forgot-whatsapp-btn"
              className="w-full rounded-full bg-brand-green hover:bg-[#007A4A] text-white py-3 font-semibold inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> Contacter le support WhatsApp
            </a>
            <button type="button" onClick={() => setForgot(false)} data-testid="partner-forgot-back" className="mt-4 w-full text-sm text-mute hover:text-ink inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Retour à la connexion
            </button>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-semibold mb-1">{t("pl.proTitle")}</h1>
            <p className="text-sm text-mute mb-5">{t("pl.proHint")}</p>

            {wrongSpace && (
              <div className="mb-4 rounded-xl border border-brand-red/30 bg-[#FFF5F6] p-3 text-sm" data-testid="partner-wrong-space-alert">
                {wrongSpace}
              </div>
            )}

            {/* Sélecteur visuel Restaurant / Livreur */}
            <div className="grid grid-cols-2 gap-2 mb-5" role="tablist">
              <button type="button" onClick={() => setKind("restaurant")} data-testid="partner-kind-restaurant" className={`rounded-2xl border-2 p-3 text-left ${kind === "restaurant" ? "border-brand-red bg-[#FFF5F6]" : "border-sand hover:border-mute"}`}>
                <Store className={`w-5 h-5 mb-1 ${kind === "restaurant" ? "text-brand-red" : "text-mute"}`} />
                <div className="font-semibold text-sm">{t("pl.restaurant")}</div>
              </button>
              <button type="button" onClick={() => setKind("driver")} data-testid="partner-kind-driver" className={`rounded-2xl border-2 p-3 text-left ${kind === "driver" ? "border-brand-red bg-[#FFF5F6]" : "border-sand hover:border-mute"}`}>
                <Bike className={`w-5 h-5 mb-1 ${kind === "driver" ? "text-brand-red" : "text-mute"}`} />
                <div className="font-semibold text-sm">{t("pl.driver")}</div>
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("pl.emailOrPhone")}</label>
                <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                  <Phone className="w-4 h-4 text-mute" />
                  <input data-testid="partner-login-identifier" type="text" required value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} className="w-full bg-transparent py-3 outline-none" placeholder="pro@email.com ou +224…" autoComplete="username" autoCapitalize="off" autoCorrect="off" spellCheck="false" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">Mot de passe</label>
                <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                  <Lock className="w-4 h-4 text-mute" />
                  <input data-testid="partner-login-password" type={show ? "text" : "password"} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full bg-transparent py-3 outline-none" placeholder="••••••••" autoComplete="current-password" />
                  <button type="button" onClick={() => setShow(!show)} className="text-mute">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </div>
              <div className="text-right">
                <button type="button" onClick={() => setForgot(true)} data-testid="partner-forgot-link" className="text-xs text-mute hover:text-brand-red">{t("auth.forgotLink")}</button>
              </div>
              <button type="submit" data-testid="partner-login-submit" disabled={loading} className="w-full rounded-full bg-ink hover:bg-black disabled:opacity-60 text-white py-3 font-semibold">
                {loading ? t("pl.loggingIn") : (kind === "restaurant" ? t("pl.accessRestaurant") : t("pl.accessDriver"))}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-sand text-sm text-mute space-y-1.5">
              <p>{t("pl.notPartner")}</p>
              <p>
                <Link to="/devenir-partenaire" data-testid="partner-goto-become-restaurant" className="text-brand-red font-semibold">{t("pl.becomeRestaurant")}</Link>
                {" · "}
                <Link to="/devenir-livreur" data-testid="partner-goto-become-driver" className="text-brand-red font-semibold">{t("pl.becomeDriver")}</Link>
              </p>
              <p className="pt-1">
                {t("pl.areYouClient")} <Link to="/login" data-testid="partner-goto-client-login" className="text-brand-red font-semibold">{t("pl.clientLogin")}</Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
